import {
  extractSubtitleLanguageDefs,
  mapMaterialsEpisodes
} from '../components/ViewProfileDialog/singleList.vue'

/** 生成接口幂等 requestId（最多 128 字符） */
export function createTtRequestId(prefix = 'tt') {
  const raw = `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 12)}`
  return raw.slice(0, 128)
}

function normalizeStr(value) {
  return String(value == null ? '' : value).trim()
}

function pickPositiveInt(value) {
  const n = Number(value)
  if (value == null || value === '' || Number.isNaN(n) || n <= 0) {
    return null
  }
  return n
}

function albumDescription(album) {
  return normalizeStr(
    (album && (album.desp || album.desc || album.description)) || ''
  )
}

function isEnglishLanguageCode(language, languageName) {
  const code = String(language || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
  // 接口示例：language = "en-us"
  if (code === 'en' || code.startsWith('en-')) return true
  const name = String(languageName || '')
    .trim()
    .toLowerCase()
  return name === '英语' || name === 'english'
}

/** 按 subtitleLangSlots 对比 orig / edited，生成 subtitles[] 变更 */
function buildSubtitleSlotChanges(orig, edited) {
  const list = []
  const origSlots = orig.subtitleLangSlots || []
  const editedSlots = edited.subtitleLangSlots || []
  editedSlots.forEach((slot) => {
    const langId = pickPositiveInt(slot.langId)
    const fileId = pickPositiveInt(slot.fileId)
    if (!langId || !fileId) return
    const prev = origSlots.find((s) => Number(s.langId) === langId)
    if (prev && Number(prev.fileId) === fileId) return
    if (!list.some((x) => x.langId === langId)) {
      list.push({ langId, fileId })
    }
  })
  return list
}

/**
 * 单集变更项 EpisodeChange（仅包含有改动的字段，见 3.md）
 */
function buildEpisodeChangeItem(orig, edited) {
  const change = {
    seriesNum: edited.episodeNo
  }
  let hasFieldChange = false

  const title = normalizeStr(edited.title)
  if (title && title !== normalizeStr(orig.title)) {
    change.title = title.slice(0, 80)
    hasFieldChange = true
  }

  const posterFileId = pickPositiveInt(edited.posterFileId)
  if (posterFileId && posterFileId !== pickPositiveInt(orig.posterFileId)) {
    change.posterFileId = posterFileId
    hasFieldChange = true
  }

  const rawFileId = pickPositiveInt(edited.rawFileId)
  if (rawFileId && rawFileId !== pickPositiveInt(orig.rawFileId)) {
    change.rawFileId = rawFileId
    hasFieldChange = true
  }

  const subtitleChanges = buildSubtitleSlotChanges(orig, edited)
  const enSlot = (edited.subtitleLangSlots || []).find((slot) =>
    isEnglishLanguageCode(slot.language, slot.languageName)
  )
  const enFileId = enSlot
    ? pickPositiveInt(enSlot.fileId)
    : pickPositiveInt(edited.enSubtitleFileId)
  const origEnSlot = (orig.subtitleLangSlots || []).find((slot) =>
    isEnglishLanguageCode(slot.language, slot.languageName)
  )
  const origEn = origEnSlot
    ? pickPositiveInt(origEnSlot.fileId)
    : pickPositiveInt(orig.enSubtitleFileId)

  if (enFileId && enFileId !== origEn) {
    const enLangId = pickPositiveInt(
      (enSlot && enSlot.langId) || edited.enSubtitleLangId || orig.enSubtitleLangId
    )
    if (enLangId) {
      if (!subtitleChanges.some((x) => x.langId === enLangId)) {
        subtitleChanges.push({ langId: enLangId, fileId: enFileId })
      }
    } else {
      change.englishSubtitleFileId = enFileId
    }
    hasFieldChange = true
  }

  if (subtitleChanges.length) {
    change.subtitles = subtitleChanges
    hasFieldChange = true
  }

  return hasFieldChange ? change : null
}

/**
 * 根据 materials 与编辑结果组装 POST /resubmit 请求体（3.md Resubmit）
 */
export function buildResubmitPayload({
  materialsRaw,
  editForm,
  editedEpisodes,
  ttVersion
}) {
  const album = (materialsRaw && materialsRaw.album) || {}
  const subtitleLanguageDefs = extractSubtitleLanguageDefs(materialsRaw)
  const originalEpisodes = mapMaterialsEpisodes(
    (materialsRaw && materialsRaw.episodes) || [],
    subtitleLanguageDefs
  )
  const origByNo = {}
  originalEpisodes.forEach((ep) => {
    origByNo[ep.episodeNo] = ep
  })

  const body = {
    requestId: createTtRequestId('resubmit'),
    changes: []
  }

  const playTitle = normalizeStr(editForm && editForm.playName)
  const albumTitle = normalizeStr(album.title)
  if (playTitle && playTitle !== albumTitle) {
    body.title = playTitle.slice(0, 80)
  }

  const coverFileId = pickPositiveInt(editForm && editForm.coverFileId)
  const origCoverFileId = pickPositiveInt(album.coverFileId)
  if (coverFileId && coverFileId !== origCoverFileId) {
    body.coverFileId = coverFileId
  }

  ;(editedEpisodes || []).forEach((edited) => {
    const orig = origByNo[edited.episodeNo]
    if (!orig) return
    const item = buildEpisodeChangeItem(orig, edited)
    if (item) body.changes.push(item)
  })

  const albumDesp = albumDescription(album)
  const despFromForm = normalizeStr(editForm && editForm.synopsis)
  const formDesp =
    despFromForm && despFromForm !== '—'
      ? despFromForm
      : albumDesp && albumDesp !== '—'
        ? albumDesp
        : ''
  const despChanged = !!formDesp && formDesp !== albumDesp
  if (formDesp && (despChanged || body.changes.length > 0)) {
    body.description = formDesp.slice(0, 500)
  }

  const tt = pickPositiveInt(ttVersion)
  if (tt) {
    body.ttVersion = tt
  }

  return body
}

export function hasResubmitPayloadChanges(body) {
  if (!body) return false
  if (body.title || body.coverFileId) return true
  if (Array.isArray(body.changes) && body.changes.length > 0) return true
  return !!normalizeStr(body.description)
}

/** 提交前校验，返回错误文案；通过则返回空字符串 */
export function validateResubmitPayload(body) {
  const changes = (body && body.changes) || []
  if (changes.length > 0 && !normalizeStr(body && body.description)) {
    return '请填写剧目简介（必填，最多 500 字）'
  }

  for (let i = 0; i < changes.length; i += 1) {
    const item = changes[i]
    const subs = item.subtitles || []
    for (let j = 0; j < subs.length; j += 1) {
      const sub = subs[j]
      if (!pickPositiveInt(sub.langId) || !pickPositiveInt(sub.fileId)) {
        return `第 ${item.seriesNum} 集字幕缺少 langId 或 fileId，请重新选择字幕文件`
      }
    }
    if (pickPositiveInt(item.englishSubtitleFileId) && subs.length) {
      return `第 ${item.seriesNum} 集请勿同时传 englishSubtitleFileId 与 subtitles 英语轨`
    }
  }
  return ''
}

/** 是否为「仅修改剧目简介」类请求 */
export function isDescriptionOnlyResubmit(body) {
  if (!body) return false
  const changes = body.changes || []
  return (
    changes.length === 0 &&
    !body.title &&
    !body.coverFileId &&
    !!normalizeStr(body.description)
  )
}

/**
 * 将后端 TT 全量校验错误转为更易理解的说明（仅改简介时无需逐集重填）
 */
export function explainResubmitFailure(message, body) {
  const base = normalizeStr(message) || '送审失败'
  if (!isDescriptionOnlyResubmit(body)) {
    return base
  }
  const hints = []
  if (/englishSubtitleFileId|itemListSubtitleList|外挂字幕/i.test(base)) {
    hints.push(
      '本次仅提交了简介（changes 为空），未改单集。报错里逐集「缺少英语字幕」是 TT 对整剧素材的全量校验，不是要求您在前端把每一集都改一遍。'
    )
  }
  if (/desp|简介|description/i.test(base) && normalizeStr(body.description)) {
    hints.push(
      '请求里已带 description。若仍报简介缺失，多为后端/TT 未先写入简介就做了全量校验，需后端排查。'
    )
  }
  if (!hints.length) {
    hints.push(
      '本次为仅修改简介的送审，参数符合 3.md（changes 可为空）。若失败，请将 requestId 与完整 message 交给后端排查。'
    )
  }
  return `${base}\n\n${hints.join('\n')}`
}
